import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, X, ChevronDown, Search, Building2, Loader2, AlertTriangle } from 'lucide-react';
import {
  getActivityCollaborators, addActivityCollaborator, removeActivityCollaborator, searchCollabOrgs,
  adminGetActivityCollaborators, adminAddActivityCollaborator, adminRemoveActivityCollaborator,
} from '../api/api';

const RELATIONSHIP_OPTIONS = [
  { value: 'co_organiser', label: 'Co-Organiser' },
  { value: 'partner',      label: 'Partner' },
  { value: 'sponsor',      label: 'Sponsor' },
  { value: 'other',        label: 'Other' },
];

const PERMISSION_OPTIONS = [
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
];

const RELATIONSHIP_DISPLAY = {
  organiser: 'Organised By',
  co_organiser: 'Co-organised by',
  partner: 'In partnership with',
  sponsor: 'Sponsored by',
};

function getRelationshipDisplay(collab) {
  if (collab.relationship_type === 'other') return collab.relationship_custom_text || 'Partner';
  return RELATIONSHIP_DISPLAY[collab.relationship_type] || 'Co-organised by';
}

function OrgDropdown({ value, onChange, disabled, activityOrg }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const debounce = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (inputRef.current) inputRef.current.focus();
    loadOrgs(query);
  }, [open]);

  useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const loadOrgs = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await searchCollabOrgs(q);
      setOrgs(res.data || []);
    } catch { setOrgs([]); }
    setLoading(false);
  }, []);

  const handleSearch = (e) => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => loadOrgs(q), 280);
  };

  const selectOrg = (org) => {
    onChange({ org_id: org.id, org_name: org.name, org_logo_url: org.logo_url, isOther: false });
    setOpen(false);
    setQuery('');
  };

  const selectOther = () => {
    onChange({ org_id: null, org_name: '', org_logo_url: null, isOther: true });
    setOpen(false);
    setQuery('');
  };

  const noResults = !loading && orgs.length === 0;
  const selected = value;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="collab-org-dropdown"
      >
        {selected ? (
          <span className="flex items-center gap-2 truncate">
            {selected.org_logo_url
              ? <img src={selected.org_logo_url} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
              : <Building2 size={14} className="text-gray-400 flex-shrink-0" />}
            <span className="truncate text-gray-800">{selected.org_name || 'Select organisation'}</span>
          </span>
        ) : (
          <span className="text-gray-400">Select organisation</span>
        )}
        <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded-md">
              <Search size={12} className="text-gray-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={handleSearch}
                placeholder="Search organisations…"
                className="text-sm bg-transparent outline-none w-full placeholder-gray-400"
                data-testid="collab-org-search"
              />
            </div>
          </div>
          <div className="max-h-52 overflow-y-auto">
            {loading && (
              <div className="flex items-center gap-2 px-3 py-3 text-xs text-gray-400">
                <Loader2 size={12} className="animate-spin" /> Searching…
              </div>
            )}
            {orgs.map(org => (
              <button
                key={org.id}
                type="button"
                onClick={() => selectOrg(org)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 transition-colors text-left"
                data-testid={`collab-org-option-${org.id}`}
              >
                {org.logo_url
                  ? <img src={org.logo_url} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                  : <Building2 size={14} className="text-gray-300 flex-shrink-0" />}
                <span className="text-sm text-gray-700 truncate">{org.name}</span>
              </button>
            ))}
            {noResults && (
              <button
                type="button"
                onClick={selectOther}
                className="w-full flex items-start gap-2 px-3 py-3 hover:bg-amber-50 transition-colors text-left border-t border-gray-100"
                data-testid="collab-org-other-option"
              >
                <AlertTriangle size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-amber-700">Did not find? <span className="font-medium">Add its name</span></span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CollaboratorRow({ collab, onRemove, isMainOrg, readOnly }) {
  const relLabel = collab.relationship_type === 'other'
    ? (collab.relationship_custom_text || 'Partner').toUpperCase()
    : { organiser: 'ORGANISED BY', co_organiser: 'CO-ORGANISED BY', partner: 'IN PARTNERSHIP WITH', sponsor: 'SPONSORED BY' }[collab.relationship_type] || 'CO-ORGANISED BY';

  return (
    <div className="mt-2" data-testid={`collab-row-${collab.id}`}>
      <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">{relLabel}</p>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full border border-gray-200 min-w-0">
          <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 border border-gray-200 bg-white flex items-center justify-center">
            {collab.org_logo_url
              ? <img src={collab.org_logo_url} alt="" className="w-full h-full object-contain" />
              : <Building2 size={10} className="text-gray-400" />}
          </div>
          <span className="text-sm font-medium text-gray-700 truncate">{collab.org_name || '—'}</span>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${
          collab.permission === 'editor'
            ? 'text-blue-700 bg-blue-50 border-blue-200'
            : 'text-gray-600 bg-gray-100 border-gray-200'
        }`}>
          {collab.permission === 'editor' ? 'Editor' : 'Viewer'}
        </span>
        {!readOnly && isMainOrg && (
          <button
            type="button"
            onClick={() => onRemove(collab.id)}
            className="p-1 hover:bg-red-50 hover:text-red-500 text-gray-400 rounded transition-colors flex-shrink-0"
            data-testid={`collab-remove-${collab.id}`}
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function AddCollaboratorForm({ activityId, onAdded, existingOrgIds, isAdmin }) {
  const [orgValue, setOrgValue] = useState(null);
  const [otherName, setOtherName] = useState('');
  const [nameWarning, setNameWarning] = useState('');
  const [relationship, setRelationship] = useState('co_organiser');
  const [customRelText, setCustomRelText] = useState('');
  const [permission, setPermission] = useState('editor');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isOther = orgValue?.isOther;

  const handleOrgChange = (val) => {
    setOrgValue(val);
    setNameWarning('');
    if (val && !val.isOther && existingOrgIds.includes(val.org_id)) {
      setError('This organisation is already a collaborator.');
    } else {
      setError('');
    }
  };

  const handleOtherName = async (e) => {
    const name = e.target.value;
    setOtherName(name);
    setOrgValue(v => v ? { ...v, org_name: name } : v);
    setNameWarning('');
    // Warn if name matches an existing org
    if (name.trim().length > 2) {
      try {
        const { searchCollabOrgs: _s } = await import('../api/api');
        // Already imported above — handled inline
      } catch {}
    }
  };

  const handleAdd = async () => {
    if (!orgValue) { setError('Select an organisation.'); return; }
    if (isOther && !otherName.trim()) { setError('Enter the organisation name.'); return; }
    if (relationship === 'other' && !customRelText.trim()) { setError('Enter the relationship description.'); return; }
    if (relationship === 'other' && customRelText.length > 100) { setError('Relationship text must be 100 characters or fewer.'); return; }

    setSaving(true);
    setError('');
    try {
      const payload = {
        org_id: isOther ? null : orgValue.org_id,
        org_name: isOther ? otherName.trim() : orgValue.org_name,
        relationship_type: relationship,
        relationship_custom_text: relationship === 'other' ? customRelText.trim() : null,
        permission,
      };
      const res = await (isAdmin ? adminAddActivityCollaborator : addActivityCollaborator)(activityId, payload);
      onAdded(res.data);
      // Reset form
      setOrgValue(null);
      setOtherName('');
      setRelationship('co_organiser');
      setCustomRelText('');
      setPermission('editor');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to add collaborator.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-dashed border-gray-300 rounded-lg p-3 space-y-3 bg-white" data-testid="add-collaborator-form">
      {/* Org selector */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Organisation</label>
        <OrgDropdown value={orgValue} onChange={handleOrgChange} />
        {isOther && (
          <input
            type="text"
            value={otherName}
            onChange={handleOtherName}
            placeholder="Organisation name"
            maxLength={120}
            className="mt-2 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500"
            data-testid="collab-other-name-input"
          />
        )}
        {nameWarning && <p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><AlertTriangle size={11} />{nameWarning}</p>}
      </div>

      {/* Relationship */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Relationship</label>
        <select
          value={relationship}
          onChange={e => setRelationship(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-green-500"
          data-testid="collab-relationship-select"
        >
          {RELATIONSHIP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {relationship === 'other' && (
          <div className="mt-2 relative">
            <input
              type="text"
              value={customRelText}
              onChange={e => setCustomRelText(e.target.value)}
              placeholder="e.g. Knowledge Partner"
              maxLength={100}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 pr-12"
              data-testid="collab-custom-rel-input"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">{customRelText.length}/100</span>
          </div>
        )}
      </div>

      {/* Permission */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Permission</label>
        <select
          value={permission}
          onChange={e => setPermission(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-green-500"
          data-testid="collab-permission-select"
        >
          {PERMISSION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <p className="text-xs text-gray-400 mt-1">
          {permission === 'editor'
            ? 'Can edit activity, manage registrations, publish/unpublish.'
            : 'Can view activity and download registrations. Cannot edit.'}
        </p>
      </div>

      {error && <p className="text-xs text-red-500" data-testid="collab-form-error">{error}</p>}

      <button
        type="button"
        onClick={handleAdd}
        disabled={saving}
        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60 transition-colors"
        data-testid="collab-add-btn"
      >
        {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
        Add
      </button>
    </div>
  );
}

export default function ActivityCollaboratorSection({ activityId, isMainOrg = true, readOnly = false, onNeedsEmailInput, isAdmin = false }) {
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    if (!activityId) { setLoading(false); return; }
    try {
      const res = await (isAdmin ? adminGetActivityCollaborators : getActivityCollaborators)(activityId);
      setCollaborators(res.data || []);
    } catch { setCollaborators([]); }
    setLoading(false);
  }, [activityId, isAdmin]);

  useEffect(() => { load(); }, [load]);

  // Close add form when entering read-only mode (after Save)
  useEffect(() => { if (readOnly) setShowAddForm(false); }, [readOnly]);

  const handleAdded = (response) => {
    // response may be a collaborator object (draft activity) or {collaborator, needs_email_input} (published)
    const collab = response?.collaborator || response;
    setCollaborators(c => [...c, collab]);
    setShowAddForm(false);
    // If activity was already published and org needs an email invite, bubble up
    if (response?.needs_email_input?.length && onNeedsEmailInput) {
      onNeedsEmailInput(response.needs_email_input);
    }
  };

  const handleRemove = async (collabId) => {
    if (!window.confirm('Remove this collaborator?')) return;
    setRemoving(collabId);
    try {
      await (isAdmin ? adminRemoveActivityCollaborator : removeActivityCollaborator)(activityId, collabId);
      setCollaborators(c => c.filter(x => x.id !== collabId));
    } catch {}
    setRemoving(null);
  };

  const existingOrgIds = collaborators.map(c => c.org_id).filter(Boolean);

  if (loading) return null;

  // Admin always has full access; org members only when isMainOrg
  const canManage = isAdmin || isMainOrg;

  return (
    <div className="space-y-0 mt-1" data-testid="collaborator-section">
      {collaborators.map(c => (
        <CollaboratorRow
          key={c.id}
          collab={c}
          onRemove={handleRemove}
          isMainOrg={canManage}
          readOnly={readOnly}
        />
      ))}

      {!readOnly && canManage && (
        <>
          {showAddForm && (
            <div className="mt-3">
              <AddCollaboratorForm
                activityId={activityId}
                onAdded={handleAdded}
                existingOrgIds={existingOrgIds}
                isAdmin={isAdmin}
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => setShowAddForm(s => !s)}
            className="flex items-center gap-1.5 text-xs text-green-600 hover:text-green-700 font-medium transition-colors mt-2"
            data-testid="add-collaborator-btn"
          >
            {showAddForm ? null : <Plus size={13} />}
            {showAddForm ? 'Cancel' : 'Add Organiser'}
          </button>
        </>
      )}
    </div>
  );
}
